import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOnlineStatus } from './useOnlineStatus'
import type { PatchOp } from './usePatchTransaction'
import { applyOptimisticPatch, usePatchTransaction } from './usePatchTransaction'
import type { ApiFetch } from '../types'
import {
  assertFinancialPatchAllowed,
  isBlockedFinancialPatch,
} from '../utils/financialPatchGuard'

interface QueuedPatchOp extends PatchOp {
  enqueuedAt: number
  baseValue: unknown
}

export interface ConflictPatchOp extends QueuedPatchOp {
  detectedAt: number
  serverValue: unknown
}

interface QueueSnapshot {
  pendingCount: number
  lastSyncedAt: number | null
  conflictCount: number
  conflicts: ConflictPatchOp[]
}

const QUEUE_EVENT = 'aera-dt-write-queue-change'
const flushLocks = new Set<string>()

function getQueueStorageKey(workspaceId: string, workerId: string) {
  return `aera-dt-write-queue:${workspaceId}:${workerId}`
}

function getMetaStorageKey(workspaceId: string, workerId: string) {
  return `aera-dt-write-queue-meta:${workspaceId}:${workerId}`
}

function getConflictStorageKey(workspaceId: string, workerId: string) {
  return `aera-dt-write-conflicts:${workspaceId}:${workerId}`
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function readQueue(workspaceId: string, workerId: string): QueuedPatchOp[] {
  if (!isBrowser()) return []

  try {
    const raw = window.localStorage.getItem(getQueueStorageKey(workspaceId, workerId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as QueuedPatchOp[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readConflicts(workspaceId: string, workerId: string): ConflictPatchOp[] {
  if (!isBrowser()) return []

  try {
    const raw = window.localStorage.getItem(getConflictStorageKey(workspaceId, workerId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as ConflictPatchOp[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeQueue(workspaceId: string, workerId: string, queue: QueuedPatchOp[]) {
  if (!isBrowser()) return

  const key = getQueueStorageKey(workspaceId, workerId)
  if (queue.length === 0) {
    window.localStorage.removeItem(key)
  } else {
    window.localStorage.setItem(key, JSON.stringify(queue))
  }
}

function writeConflicts(workspaceId: string, workerId: string, conflicts: ConflictPatchOp[]) {
  if (!isBrowser()) return

  const key = getConflictStorageKey(workspaceId, workerId)
  if (conflicts.length === 0) {
    window.localStorage.removeItem(key)
  } else {
    window.localStorage.setItem(key, JSON.stringify(conflicts))
  }
}

function readLastSyncedAt(workspaceId: string, workerId: string) {
  if (!isBrowser()) return null

  try {
    const raw = window.localStorage.getItem(getMetaStorageKey(workspaceId, workerId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { lastSyncedAt?: number }
    return typeof parsed?.lastSyncedAt === 'number' ? parsed.lastSyncedAt : null
  } catch {
    return null
  }
}

function writeLastSyncedAt(workspaceId: string, workerId: string, lastSyncedAt: number) {
  if (!isBrowser()) return

  window.localStorage.setItem(
    getMetaStorageKey(workspaceId, workerId),
    JSON.stringify({ lastSyncedAt }),
  )
}

function getSnapshot(workspaceId: string, workerId: string): QueueSnapshot {
  const conflicts = readConflicts(workspaceId, workerId)
  return {
    pendingCount: readQueue(workspaceId, workerId).length,
    lastSyncedAt: readLastSyncedAt(workspaceId, workerId),
    conflictCount: conflicts.length,
    conflicts,
  }
}

function emitQueueChange(workspaceId: string, workerId: string) {
  if (!isBrowser()) return

  window.dispatchEvent(new CustomEvent(QUEUE_EVENT, { detail: { workspaceId, workerId } }))
}

function isSameComparableValue(left: unknown, right: unknown) {
  return String(left ?? '') === String(right ?? '')
}

function getRecordFieldValue(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  workerId: string,
  recordId: string,
  field: string,
) {
  const targetId = String(recordId ?? '')
  const queries = queryClient.getQueriesData({ queryKey: ['transactions', workerId, workspaceId] })
  for (const [, data] of queries) {
    const pages = (data as any)?.pages
    if (!Array.isArray(pages)) continue

    for (const page of pages) {
      const match = page?.data?.find(
        (record: any) => String(record?.record_id ?? '') === targetId,
      )
      if (match) return match[field]
    }
  }

  return undefined
}

function upsertQueueOp(workspaceId: string, workerId: string, op: QueuedPatchOp) {
  const queue = readQueue(workspaceId, workerId)
  const deduped = queue.filter(
    (item) => !(item.recordId === op.recordId && item.field === op.field),
  )

  deduped.push({ ...op, enqueuedAt: Date.now() })
  writeQueue(workspaceId, workerId, deduped)
  emitQueueChange(workspaceId, workerId)
}

function upsertConflictOp(workspaceId: string, workerId: string, op: ConflictPatchOp) {
  const conflicts = readConflicts(workspaceId, workerId)
  const deduped = conflicts.filter(
    (item) => !(item.recordId === op.recordId && item.field === op.field),
  )

  deduped.push(op)
  writeConflicts(workspaceId, workerId, deduped)
  emitQueueChange(workspaceId, workerId)
}

export function useWriteQueue(
  workspaceId: string,
  workerId: string,
  apiFetch?: ApiFetch,
) {
  const isOnline = useOnlineStatus()
  const queryClient = useQueryClient()
  const { mutateAsync: patchField } = usePatchTransaction(workspaceId, workerId, apiFetch)

  const [snapshot, setSnapshot] = useState<QueueSnapshot>(() => getSnapshot(workspaceId, workerId))
  const [isFlushing, setIsFlushing] = useState(false)
  const [isResolvingConflicts, setIsResolvingConflicts] = useState(false)
  const queueKey = useMemo(() => getQueueStorageKey(workspaceId, workerId), [workspaceId, workerId])

  useEffect(() => {
    setSnapshot(getSnapshot(workspaceId, workerId))
  }, [workspaceId, workerId])

  useEffect(() => {
    if (!isBrowser()) return

    const syncSnapshot = () => setSnapshot(getSnapshot(workspaceId, workerId))

    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === getQueueStorageKey(workspaceId, workerId) ||
        event.key === getMetaStorageKey(workspaceId, workerId) ||
        event.key === getConflictStorageKey(workspaceId, workerId)
      ) {
        syncSnapshot()
      }
    }

    const handleQueueChange = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId: string; workerId: string }>).detail
      if (detail?.workspaceId === workspaceId && detail?.workerId === workerId) {
        syncSnapshot()
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(QUEUE_EVENT, handleQueueChange)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(QUEUE_EVENT, handleQueueChange)
    }
  }, [workspaceId, workerId])

  const patchWithQueue = async (op: PatchOp) => {
    assertFinancialPatchAllowed(op.field, op.value)

    // LIFF-00: never park money-lifecycle fields in the generic offline queue.
    // (assertFinancialPatchAllowed already throws for those; keep the guard explicit.)
    if (!isOnline && isBlockedFinancialPatch(op.field, op.value)) {
      throw new Error('ออฟไลน์ — ไม่สามารถแก้ข้อมูลเงินผ่านคิวออฟไลน์ได้')
    }

    if (!isOnline) {
      const baseValue = getRecordFieldValue(queryClient, workspaceId, workerId, op.recordId, op.field)
      applyOptimisticPatch(queryClient, workspaceId, workerId, op)
      upsertQueueOp(workspaceId, workerId, { ...op, baseValue, enqueuedAt: Date.now() })
      return
    }

    await patchField(op)
    emitQueueChange(workspaceId, workerId)
  }

  useEffect(() => {
    if (!isBrowser()) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (readQueue(workspaceId, workerId).length > 0) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [workspaceId, workerId])

  useEffect(() => {
    if (!isOnline || flushLocks.has(queueKey)) return

    const queue = readQueue(workspaceId, workerId)
    if (queue.length === 0) return

    flushLocks.add(queueKey)
    setIsFlushing(true)

    const flush = async () => {
      const remaining = [...queue]

      await queryClient.invalidateQueries({ queryKey: ['transactions', workerId, workspaceId] })
      await queryClient.refetchQueries({ queryKey: ['transactions', workerId, workspaceId] })

      while (remaining.length > 0) {
        const next = remaining[0]

        if (isBlockedFinancialPatch(next.field, next.value)) {
          console.warn('[useWriteQueue] dropping blocked financial patch', {
            recordId: next.recordId,
            field: next.field,
          })
          remaining.shift()
          writeQueue(workspaceId, workerId, remaining)
          emitQueueChange(workspaceId, workerId)
          continue
        }

        const serverValue = getRecordFieldValue(queryClient, workspaceId, workerId, next.recordId, next.field)

        // Cache miss after a long offline session: we cannot compare versions safely here.
        // Fall back to optimistic replay instead of misclassifying the op as a conflict.
        if (serverValue === undefined) {
          console.warn('[useWriteQueue] cache miss on conflict check', {
            recordId: next.recordId,
            field: next.field,
          })
        }

        if (
          serverValue !== undefined &&
          !isSameComparableValue(serverValue, next.baseValue) &&
          !isSameComparableValue(serverValue, next.value)
        ) {
          remaining.shift()
          writeQueue(workspaceId, workerId, remaining)
          upsertConflictOp(workspaceId, workerId, {
            ...next,
            detectedAt: Date.now(),
            serverValue,
          })
          continue
        }

        try {
          await patchField(next)
          remaining.shift()
          writeQueue(workspaceId, workerId, remaining)
          writeLastSyncedAt(workspaceId, workerId, Date.now())
          emitQueueChange(workspaceId, workerId)
        } catch (error) {
          console.error('Failed to flush patch op', next, error)
          writeQueue(workspaceId, workerId, remaining)
          emitQueueChange(workspaceId, workerId)
          break
        }
      }

      flushLocks.delete(queueKey)
      setIsFlushing(false)
      setSnapshot(getSnapshot(workspaceId, workerId))
    }

    void flush()
  }, [isOnline, patchField, queryClient, queueKey, workspaceId, workerId])

  const resolveConflicts = async (strategy: 'keep-mine' | 'use-server') => {
    const conflicts = readConflicts(workspaceId, workerId)
    if (conflicts.length === 0) return

    if (strategy === 'use-server') {
      writeConflicts(workspaceId, workerId, [])
      emitQueueChange(workspaceId, workerId)
      await queryClient.invalidateQueries({ queryKey: ['transactions', workerId, workspaceId] })
      return
    }

    if (!isOnline) return

    setIsResolvingConflicts(true)

    try {
      const remaining = [...conflicts]

      while (remaining.length > 0) {
        const next = remaining[0]

        try {
          await patchField(next)
          remaining.shift()
          writeConflicts(workspaceId, workerId, remaining)
          writeLastSyncedAt(workspaceId, workerId, Date.now())
          emitQueueChange(workspaceId, workerId)
        } catch (error) {
          console.error('Failed to resolve conflict patch op', next, error)
          writeConflicts(workspaceId, workerId, remaining)
          emitQueueChange(workspaceId, workerId)
          break
        }
      }
    } finally {
      setIsResolvingConflicts(false)
      setSnapshot(getSnapshot(workspaceId, workerId))
    }
  }

  return {
    patchWithQueue,
    pendingCount: snapshot.pendingCount,
    lastSyncedAt: snapshot.lastSyncedAt,
    conflictCount: snapshot.conflictCount,
    conflicts: snapshot.conflicts,
    isFlushing,
    isResolvingConflicts,
    resolveConflicts,
  }
}
